import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getContentfulImageUrl } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { LINKS, NAVIGATION_CONTENT_TYPE } from '../../../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../../../NavbarV2.messages';
import giftCardsImage from './assets/gift-cards.jpg';

import type { Asset, Entry } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavbarContent = () => {
  const { formatMessage } = useIntl();

  const { data: navigationData } = useContentfulContentTypeEntry<NavigationContent>({
    contentType: NAVIGATION_CONTENT_TYPE,
    include: 2,
  });

  // ─── Derived ────────────────────────────────────────────────────────────────

  const contentfulLinks = navigationData?.fields?.moreMenuNavLinks;

  const moreMenuNavLinks = useMemo(() => {
    if (!contentfulLinks?.length) {
      return [
        {
          url: LINKS.GIFT,
          title: formatMessage(messages.buyGiftCards),
          text: formatMessage(messages.giftCardsDescription),
          imageAlt: formatMessage(messages.giftCardsImageAlt),
          imageSrc: giftCardsImage,
        },
      ];
    }

    return contentfulLinks.map((link) => {
      const { image, ...linkFields } = link.fields;

      return {
        ...linkFields,
        imageAlt: image.fields.description,
        imageSrc: getContentfulImageUrl(image.fields.file.url, CONTENTFUL_IMAGE_OPTIONS),
      };
    });
  }, [contentfulLinks, formatMessage]);

  // ───────────────────────────────────────────────────────────────────────────

  return { moreMenuNavLinks };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENTFUL_IMAGE_OPTIONS = { w: 160 };

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationContent = {
  moreMenuNavLinks: Entry<NavigationLink>[];
};

type NavigationLink = {
  title: string;
  text?: string;
  url: string;
  image: Asset;
};
